import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { FilterIcon } from 'components/common/icons';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, Form, FormInstance, Row, Table, Typography } from 'components';
import { useItemSuppliers } from './useItemSuppliers';
import styles from './styles.module.scss';
import { Filters } from './filters';

const { Text } = Typography;

type EntityTableType = {
  name: string;
  id: string;
};

export interface ItemSupplierDataType {
  source: string;
  price: number;
  supplier: EntityTableType;
  createDate: string;
  expiryDate: string;
}

const SOURCE_NAME_MAPPING: Record<string, string> = {
  PRICE_LIST: 'Price-list',
};

export const ItemSuppliers: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    onRedirectToSupplier,
    isLoading,
    pagination,
    data,
    total,
    applyFilters,
    resetFilters,
  } = useItemSuppliers();

  const columns: ColumnsType<ItemSupplierDataType> = [
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      ellipsis: true,
      width: 180,
      render: (value) => {
        return (
          <Button
            size={'small'}
            type={'text'}
            variant={'text'}
            className={'underline-button'}
            onClick={() => onRedirectToSupplier(value.id)}
          >
            <Text underline type={'secondary'} ellipsis={{ tooltip: true }} style={{ maxWidth: '180px' }}>
              {value?.name}
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 110,
      ellipsis: true,
      render: (value: string) => {
        return <Text ellipsis={{ tooltip: true }}>{SOURCE_NAME_MAPPING[value] || ''}</Text>;
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      // sorter: true,
      width: 200,
      ellipsis: true,
      render: (value) => {
        return <Text ellipsis={{ tooltip: true }}>{value}</Text>;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreateDate',
      key: 'createDate',
      // sorter: true,
      width: 130,
    },
    {
      title: 'Expiry Date',
      dataIndex: 'formattedExpiryDate',
      key: 'expiryDate',
      width: 200,
    },
  ];

  return (
    <HasAccess role={PERMISSIONS.items.supplier.read}>
      <Row gutter={[0, 24]}>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.brands.brand.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
        <Filters
          form={filtersForm}
          drawerOpen={openFilters}
          closeDrawer={onCloseFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
        />
      </Row>
    </HasAccess>
  );
};
